import { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Search, 
  Grid, 
  List, 
  Download, 
  Eye, 
  FileText, 
  Image, 
  File, 
  Filter, 
  X, 
  Trash2,
  Share2,
  Calendar,
  User,
  Building2,
  Sparkles
} from 'lucide-react'
import { GaleriaService } from '../../services'
import { useAuth } from '../../contexts/AuthContext'
import { useDarkMode } from '../../contexts/DarkModeContext'

interface FileItem {
  id: number
  nome: string
  tipo: string
  ticket: string
  data: string
  departamento: string
  usuario: string
  descricao: string
  tags: string[]
  thumbnail?: string
  caminho: string
  ticket_id?: number
  resposta_id?: number
}

interface Pagination {
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  totalPages: number;
}

export default function Gallery() {
  const { usuario } = useAuth();
  const { isDarkMode } = useDarkMode();
  const [files, setFiles] = useState<FileItem[]>([])
  const [filteredFiles, setFilteredFiles] = useState<FileItem[]>([])
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [selectedFile, setSelectedFile] = useState<FileItem | null>(null)
  const [showPreview, setShowPreview] = useState(false)
  const [filters, setFilters] = useState<{
    tipo: string
    departamento: string
    usuario: string
    dataInicio: string
    dataFim: string
  }>({
    tipo: '',
    departamento: '',
    usuario: '',
    dataInicio: '',
    dataFim: '',
  })
  const [showFilters, setShowFilters] = useState(false)
  const [sortBy, setSortBy] = useState<'data' | 'nome' | 'tipo'>('data')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [selectedFiles, setSelectedFiles] = useState<Set<number>>(new Set())
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    itemsPerPage: 8,
    totalItems: 0,
    totalPages: 0
  })

  useEffect(() => {
    loadGalleryData()
  }, [])

  const loadGalleryData = async () => {
    try {
      setIsLoading(true)
      
      const response = await GaleriaService.getGallery()
      
      if (response.success && response.data) {

        // Converter objeto em array se necessário
        let dataArray = response.data
        if (!Array.isArray(response.data)) {
          dataArray = Object.values(response.data)
        }
        
        // Mapear dados da API para o formato esperado pelo componente
        const apiFiles = dataArray.map(file => ({
          id: file.id,
          nome: file.nome,
          tipo: file.tipo,
          ticket: file.ticket,
          data: file.data,
          departamento: file.departamento,
          usuario: file.usuario,
          descricao: file.descricao,
          tags: file.tags || [],
          thumbnail: file.thumbnail,
          caminho: file.caminho,
          ticket_id: file.ticket_id,
          resposta_id: file.resposta_id
        }))
        
        setFiles(apiFiles)
        setFilteredFiles(apiFiles)
      } else {
        setFiles([])
        setFilteredFiles([])
      }
      
      
    } catch (error) {
      console.error('Erro ao carregar dados da galeria:', error)
      setFiles([])
      setFilteredFiles([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    // Aplicar filtros localmente quando searchTerm, sortBy ou sortOrder mudarem
    if (files.length > 0) {
      applyFiltersLocally()
    }
  }, [files, searchTerm, sortBy, sortOrder])

  // Aplicar filtros via API quando os filtros principais mudarem
  useEffect(() => {
    if (files.length > 0 && (filters.tipo || filters.departamento || filters.usuario || filters.dataInicio || filters.dataFim)) {
      applyFilters()
    }
  }, [filters.tipo, filters.departamento, filters.usuario, filters.dataInicio, filters.dataFim])

  // Atualizar paginação quando os filtros mudarem
  useEffect(() => {
    const totalItems = filteredFiles.length;
    const totalPages = Math.ceil(totalItems / pagination.itemsPerPage);
    
    setPagination(prev => ({
      ...prev,
      currentPage: 1,
      totalItems,
      totalPages
    }));
  }, [filteredFiles, pagination.itemsPerPage]);

  const getFileIcon = (tipo: string) => {
    switch (tipo) {
      case 'imagem': return <Image className="text-blue-500 dark:text-blue-400 w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10" />
      case 'documento': return <FileText className="text-red-500 dark:text-red-400 w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10" />
      default: return <File className="text-gray-500 dark:text-gray-400 w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10" />
    }
  }

  const getFileTypeColor = (tipo: string) => {
    switch (tipo) {
      case 'imagem': return 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800'
      case 'documento': return 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800'
      default: return 'bg-gray-100 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600'
    }
  }

  const handleFilePreview = (file: FileItem) => {
    setSelectedFile(file)
    setShowPreview(true)
  }

  const handleDownload = async (file: FileItem) => {
    try {
      // Tentar usar a API real para download
      if (file.id && typeof file.id === 'number') {
        const response = await GaleriaService.downloadGalleryFile(file.id)
        // A API retorna o arquivo diretamente, então criamos um link para download
        const blob = new Blob([response], { type: 'application/octet-stream' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = file.nome
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        return
      }
    } catch (apiError) {
    }
    
    // Fallback para download simulado
    const link = document.createElement('a')
    link.href = `#${file.nome}`
    link.download = file.nome
    link.click()
  }

  const toggleFileSelection = (fileId: number) => {
    const newSelected = new Set(selectedFiles)
    if (newSelected.has(fileId)) {
      newSelected.delete(fileId)
    } else {
      newSelected.add(fileId)
    }
    setSelectedFiles(newSelected)
  }

  const handleDeleteFile = async (fileId: number) => {
    try {
      // Tentar usar a API real para deletar
      if (typeof fileId === 'number') {
        await GaleriaService.deleteGalleryFile(fileId)
        // Remover arquivo da lista local
        setFiles(prev => prev.filter(f => f.id !== fileId))
        setFilteredFiles(prev => prev.filter(f => f.id !== fileId))
        setSelectedFiles(prev => {
          const newSelected = new Set(prev)
          newSelected.delete(fileId)
          return newSelected
        })
        return
      }
    } catch (apiError) {
    }
    
    // Fallback para remoção simulada
    setFiles(prev => prev.filter(f => f.id !== fileId))
    setFilteredFiles(prev => prev.filter(f => f.id !== fileId))
    setSelectedFiles(prev => {
      const newSelected = new Set(prev)
      newSelected.delete(fileId)
      return newSelected
    })
  }

  const handleDeleteSelected = async () => {
    if (selectedFiles.size === 0) return
    
    const confirmed = window.confirm(`Tem certeza que deseja remover ${selectedFiles.size} arquivo(s)?`)
    if (!confirmed) return
    
    for (const fileId of selectedFiles) {
      await handleDeleteFile(fileId)
    }
    
    setSelectedFiles(new Set())
  }

  const applyFiltersLocally = () => {
    let filtered = files.filter(file => {
      const matchesSearch = file.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           file.descricao.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           file.ticket.toLowerCase().includes(searchTerm.toLowerCase())
      
      return matchesSearch
    })

    // Aplicar ordenação
    filtered.sort((a, b) => {
      let aValue: any, bValue: any
      
      switch (sortBy) {
        case 'nome':
          aValue = a.nome.toLowerCase()
          bValue = b.nome.toLowerCase()
          break
        case 'tipo':
          aValue = a.tipo.toLowerCase()
          bValue = b.tipo.toLowerCase()
          break
        case 'data':
        default:
          aValue = new Date(a.data)
          bValue = new Date(b.data)
          break
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1
      } else {
        return aValue < bValue ? 1 : -1
      }
    })

    setFilteredFiles(filtered)
    setPagination(prev => ({ ...prev, currentPage: 1 })) // Reset para primeira página quando filtros mudam
  }

  const applyFilters = () => {
    let filtered = files.filter(file => {
      const matchesTipo = !filters.tipo || file.tipo === filters.tipo
      const matchesDepartamento = !filters.departamento || file.departamento === filters.departamento
      const matchesUsuario = !filters.usuario || file.usuario === filters.usuario
      
      let matchesData = true
      if (filters.dataInicio) {
        matchesData = matchesData && new Date(file.data) >= new Date(filters.dataInicio)
      }
      if (filters.dataFim) {
        matchesData = matchesData && new Date(file.data) <= new Date(filters.dataFim)
      }
      
      return matchesTipo && matchesDepartamento && matchesUsuario && matchesData
    })

    setFilteredFiles(filtered)
    setPagination(prev => ({ ...prev, currentPage: 1 })) // Reset para primeira página
  }

  const clearFilters = () => {
    setFilters({
      tipo: '',
      departamento: '',
      usuario: '',
      dataInicio: '',
      dataFim: '',
    })
    setSearchTerm('')
    setPagination(prev => ({ ...prev, currentPage: 1 })) // Reset para primeira página
    // Recarregar dados sem filtros
    loadGalleryData()
  }

  const getDepartamentos = () => [...new Set(files.map(f => f.departamento))]
  const getUsuarios = () => [...new Set(files.map(f => f.usuario))]
  const getTipos = () => {
    const tiposPermitidos = ['imagem', 'documento']
    return [...new Set(files.map(f => f.tipo).filter(tipo => tiposPermitidos.includes(tipo)))]
  }

  // Lógica de paginação
  const totalItems = filteredFiles.length;
  const totalPages = Math.ceil(totalItems / pagination.itemsPerPage);

  // Obter itens da página atual
  const getCurrentPageItems = () => {
    const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
    const endIndex = startIndex + pagination.itemsPerPage;
    return filteredFiles.slice(startIndex, endIndex);
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({
      ...prev,
      currentPage: page
    }));
  };

  const getDocumentIcon = (nomeArquivo: string) => {
    const extensao = nomeArquivo.toLowerCase().split('.').pop()
    switch (extensao) {
      case 'pdf':
        return <FileText className="text-red-500 dark:text-red-400 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12" />
      case 'doc':
      case 'docx':
        return <FileText className="text-blue-500 dark:text-blue-400 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12" />
      case 'xls':
      case 'xlsx':
        return <FileText className="text-green-500 dark:text-green-400 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12" />
      case 'ppt':
      case 'pptx':
        return <FileText className="text-orange-500 dark:text-orange-400 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12" />
      default:
        return <FileText className="text-red-500 dark:text-red-400 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12" />
    }
  }

  const renderFileThumbnail = (file: FileItem) => {
    // Se for documento, mostrar ícone específico
    if (file.tipo === 'documento') {
      return (
        <div className="w-full h-32 sm:h-40 md:h-48 lg:h-56 bg-gradient-to-br from-gray-50 dark:from-gray-800 via-gray-100 dark:via-gray-700 to-gray-50 dark:to-gray-800 rounded-xl flex items-center justify-center border border-gray-200 dark:border-gray-700 group-hover:border-primary-300 dark:group-hover:border-primary-600 transition-colors">
          <div className="text-center">
            {getDocumentIcon(file.nome)}
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-2 font-medium">DOCUMENTO</p>
          </div>
        </div>
      )
    }

    // Se for imagem, mostrar thumbnail ou ícone
    if (file.tipo === 'imagem') {
      if (file.thumbnail) {
        return (
          <div className="relative w-full h-32 sm:h-40 md:h-48 lg:h-56 bg-gray-100 dark:bg-gray-800 rounded-xl overflow-hidden group-hover:ring-2 group-hover:ring-primary-300 dark:group-hover:ring-primary-600 transition-all">
            <img 
              src={`${import.meta.env.VITE_API_URL}/${file.thumbnail}`}
              alt={file.nome}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </div>
        )
      } else {
        return (
          <div className="w-full h-32 sm:h-40 md:h-48 lg:h-56 bg-gradient-to-br from-blue-50 dark:from-blue-900/30 via-blue-100 dark:via-blue-900/20 to-blue-50 dark:to-blue-900/30 rounded-xl flex items-center justify-center border border-blue-200 dark:border-blue-800 group-hover:border-blue-300 dark:group-hover:border-blue-600 transition-colors">
            <div className="text-center">
              {getFileIcon(file.tipo)}
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-2 font-medium">{file.tipo.toUpperCase()}</p>
            </div>
          </div>
        )
      }
    }

    // Fallback para outros tipos de arquivo
    return (
      <div className="w-full h-32 sm:h-40 md:h-48 lg:h-56 bg-gradient-to-br from-gray-50 dark:from-gray-800 via-gray-100 dark:via-gray-700 to-gray-50 dark:to-gray-800 rounded-xl flex items-center justify-center border border-gray-200 dark:border-gray-700 group-hover:border-primary-300 dark:group-hover:border-primary-600 transition-colors">
        <div className="text-center">
          {getFileIcon(file.tipo)}
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-2 font-medium">{file.tipo.toUpperCase()}</p>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-200 dark:border-primary-800 border-t-primary-600 dark:border-t-primary-400"></div>
          <p className="text-gray-600 dark:text-gray-400 font-medium">Carregando galeria...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Galeria de Arquivos
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Visualize, gerencie e organize todos os arquivos do sistema
            </p>
          </div>
          <div className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-all duration-200 ${
                viewMode === 'grid' 
                  ? 'bg-white dark:bg-gray-600 text-primary-600 dark:text-primary-400 shadow-sm' 
                  : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400'
              }`}
            >
              <Grid size={18} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-all duration-200 ${
                viewMode === 'list' 
                  ? 'bg-white dark:bg-gray-600 text-primary-600 dark:text-primary-400 shadow-sm' 
                  : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400'
              }`}
            >
              <List size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400">
              <FileText className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total de Arquivos</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{filteredFiles.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400">
              <Image className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Imagens</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {filteredFiles.filter(f => f.tipo === 'imagem').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400">
              <FileText className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Documentos</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {filteredFiles.filter(f => f.tipo === 'documento').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3">
            {/* Busca e Filtros */}
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
                <input
                  type="text"
                  placeholder="Buscar arquivos por nome, descrição ou ticket..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-400 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                />
              </div>
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700/50 transition-colors border border-gray-300 dark:border-gray-600"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filtros
              </button>
            </div>
            
            {/* Ordenação e Ações */}
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'data' | 'nome' | 'tipo')}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-400 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                >
                  <option value="data">Ordenar por Data</option>
                  <option value="nome">Ordenar por Nome</option>
                  <option value="tipo">Ordenar por Tipo</option>
                </select>
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-sm"
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>
              
              {selectedFiles.size > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400 hidden sm:inline">{selectedFiles.size} selecionado(s)</span>
                  <button 
                    onClick={handleDeleteSelected}
                    className="inline-flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700/50 transition-colors border border-gray-300 dark:border-gray-600"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Limpar
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Filtros Avançados */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="pt-4 sm:pt-6 border-t border-gray-200 dark:border-gray-700"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tipo de Arquivo</label>
                    <select
                      value={filters.tipo}
                      onChange={(e) => setFilters(prev => ({ ...prev, tipo: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-400 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                    >
                      <option value="">Todos os tipos</option>
                      {getTipos().map(tipo => (
                        <option key={tipo} value={tipo}>{tipo.toUpperCase()}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Departamento</label>
                    <select
                      value={filters.departamento}
                      onChange={(e) => setFilters(prev => ({ ...prev, departamento: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-400 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                    >
                      <option value="">Todos os departamentos</option>
                      {getDepartamentos().map(dept => (
                        <option key={dept} value={dept}>{dept}</option>
                      ))}
                    </select>
                  </div>
                  {(usuario?.nivel === 'admin' || usuario?.nivel === 'dev') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Usuário</label>
                      <select
                        value={filters.usuario}
                        onChange={(e) => setFilters(prev => ({ ...prev, usuario: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-400 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                      >
                        <option value="">Todos os usuários</option>
                        {getUsuarios().map(user => (
                          <option key={user} value={user}>{user}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Data Início</label>
                    <input
                      type="date"
                      value={filters.dataInicio}
                      onChange={(e) => setFilters(prev => ({ ...prev, dataInicio: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-400 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Data Fim</label>
                    <input
                      type="date"
                      value={filters.dataFim}
                      onChange={(e) => setFilters(prev => ({ ...prev, dataFim: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-400 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                    />
                  </div>
                </div>
                <div className="flex justify-end mt-4">
                  <button 
                    onClick={clearFilters} 
                    className="inline-flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700/50 transition-colors border border-gray-300 dark:border-gray-600"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Limpar
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Files Grid/List */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-soft overflow-hidden">
        {viewMode === 'grid' ? (
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4 sm:gap-6">
            {getCurrentPageItems().map((file) => (
              <motion.div
                key={file.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="group relative bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-primary-400 dark:hover:border-primary-600 hover:shadow-xl transition-all duration-300 overflow-hidden"
                whileHover={{ y: -4 }}
              >
                {/* Selection Checkbox */}
                <div className="absolute top-3 right-3 z-10">
                  <input
                    type="checkbox"
                    checked={selectedFiles.has(file.id)}
                    onChange={() => toggleFileSelection(file.id)}
                    className="w-5 h-5 text-primary-600 dark:text-primary-400 rounded border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 cursor-pointer"
                  />
                </div>

                {/* File Thumbnail */}
                <div className="cursor-pointer" onClick={() => handleFilePreview(file)}>
                  {renderFileThumbnail(file)}
                </div>

                {/* File Info */}
                <div className="p-4">
                  <div className="flex items-center justify-center mb-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getFileTypeColor(file.tipo)}`}>
                      {file.tipo.toUpperCase()}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-2 truncate flex items-center gap-1">
                    <FileText size={12} />
                    {file.ticket}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-3 truncate flex items-center gap-2">
                    <Building2 size={12} />
                    {file.departamento}
                  </div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4 truncate">{file.nome}</div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <button 
                      onClick={() => handleFilePreview(file)}
                      className="p-2 rounded-lg bg-primary-500 dark:bg-primary-600 text-white hover:bg-primary-600 dark:hover:bg-primary-700 transition-all shadow-lg shadow-primary-500/30"
                      title="Visualizar"
                    >
                      <Eye size={16} />
                    </button>
                    <button 
                      onClick={() => handleDownload(file)}
                      className="p-2 rounded-lg bg-blue-500 dark:bg-blue-600 text-white hover:bg-blue-600 dark:hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/30"
                      title="Download"
                    >
                      <Download size={16} />
                    </button>
                  </div>
                </div>
              </motion.div>
              ))}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedFiles.size === getCurrentPageItems().length && getCurrentPageItems().length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedFiles(new Set(getCurrentPageItems().map(f => f.id)))
                        } else {
                          setSelectedFiles(new Set())
                        }
                      }}
                      className="w-4 h-4 text-primary-600 dark:text-primary-400 rounded border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Arquivo</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Tipo</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell">Ticket</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider hidden lg:table-cell">Departamento</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider hidden xl:table-cell">Usuário</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider hidden sm:table-cell">Data</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {getCurrentPageItems().map((file) => (
                  <tr key={file.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedFiles.has(file.id)}
                        onChange={() => toggleFileSelection(file.id)}
                        className="w-4 h-4 text-primary-600 dark:text-primary-400 rounded border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {file.tipo === 'documento' ? (
                          <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                            {getDocumentIcon(file.nome)}
                          </div>
                        ) : file.thumbnail ? (
                          <img 
                            src={`${import.meta.env.VITE_API_URL}/${file.thumbnail}`}
                            alt=""
                            className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                            {getFileIcon(file.tipo)}
                          </div>
                        )}
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-xs">{file.nome}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getFileTypeColor(file.tipo)}`}>
                        {file.tipo.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <a
                        href={`/tickets/${file.ticket_id}`}
                        className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:underline transition-colors"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        #{file.ticket_id}
                      </a>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <span className="text-sm text-gray-700 dark:text-gray-300">{file.departamento}</span>
                    </td>
                    <td className="px-4 py-3 hidden xl:table-cell text-sm text-gray-600 dark:text-gray-400">{file.usuario}</td>
                    <td className="px-4 py-3 hidden sm:table-cell text-sm text-gray-500 dark:text-gray-400">
                      {new Date(file.data).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => handleFilePreview(file)}
                          className="p-2 rounded-lg bg-primary-500 dark:bg-primary-600 text-white hover:bg-primary-600 dark:hover:bg-primary-700 transition-all"
                          title="Visualizar"
                        >
                          <Eye size={16} />
                        </button>
                        <button 
                          onClick={() => handleDownload(file)}
                          className="p-2 rounded-lg bg-blue-500 dark:bg-blue-600 text-white hover:bg-blue-600 dark:hover:bg-blue-700 transition-all"
                          title="Download"
                        >
                          <Download size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {getCurrentPageItems().length === 0 && (
          <div className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
            <p className="text-lg font-medium text-gray-900 dark:text-gray-100">Nenhum arquivo encontrado</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Tente ajustar os filtros de busca ou fazer upload de novos arquivos</p>
          </div>
        )}

        {/* Paginação */}
        {totalPages > 1 && (
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 text-center sm:text-left">
                  Mostrando {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} a {Math.min(pagination.currentPage * pagination.itemsPerPage, totalItems)} de {totalItems} resultados
                </div>
                <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-2">
                  {/* Botão Anterior */}
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage === 1}
                    className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg border w-full sm:w-auto ${pagination.currentPage === 1
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-600 cursor-not-allowed'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                  >
                    ← Anterior
                  </button>

                  {/* Números das páginas */}
                  <div className="flex items-center justify-center space-x-1 flex-wrap">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= pagination.currentPage - 1 && page <= pagination.currentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-lg border min-w-[2.5rem] sm:min-w-[2.75rem] ${page === pagination.currentPage
                              ? 'bg-blue-600 dark:bg-primary-500 text-white border-blue-600 dark:border-primary-500'
                              : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                              }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (
                        page === pagination.currentPage - 2 ||
                        page === pagination.currentPage + 2
                      ) {
                        return <span key={page} className="px-1 sm:px-2 text-gray-400 text-xs">...</span>;
                      }
                      return null;
                    })}
                  </div>

                  {/* Botão Próximo */}
                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={pagination.currentPage === totalPages}
                    className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg border w-full sm:w-auto ${pagination.currentPage === totalPages
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-600 cursor-not-allowed'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                  >
                    Próximo →
                  </button>
                </div>
              </div>
          </div>
        )}
      </div>

      {/* File Preview Modal */}
      <AnimatePresence>
        {showPreview && selectedFile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/70 dark:bg-black/90 backdrop-blur-sm"
            onClick={() => setShowPreview(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700"
              onClick={e => e.stopPropagation()}
            >
              {/* Cabeçalho */}
              <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 dark:from-gray-900 to-white dark:to-gray-800 rounded-t-2xl">
                <div className="min-w-0 flex-1 pr-4">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 truncate">{selectedFile.nome}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                      <FileText size={12} />
                      {selectedFile.ticket}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">•</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                      <Building2 size={12} />
                      {selectedFile.departamento}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
                  title="Fechar"
                >
                  <X size={20} className="text-gray-500 dark:text-gray-400" />
                </button>
              </div>
              {/* Conteúdo */}
              <div className="px-6 py-6">
                {/* Visualização do arquivo */}
                <div className="mb-6">
                  {selectedFile.thumbnail ? (
                    <div className="bg-gray-100 dark:bg-gray-900/50 rounded-xl p-4 flex justify-center items-center min-h-[300px] max-h-[500px] overflow-hidden">
                      <img 
                        src={`${import.meta.env.VITE_API_URL}/${selectedFile.thumbnail}`} 
                        alt={selectedFile.nome}
                        className="rounded-lg max-w-full max-h-[500px] object-contain transition-transform duration-300 hover:scale-105"
                      />
                    </div>
                  ) : (
                    <div className="bg-gradient-to-br from-gray-100 dark:from-gray-900/50 to-gray-50 dark:to-gray-800/50 rounded-xl p-12 flex flex-col items-center justify-center min-h-[300px]">
                      <div className="mb-4">{getFileIcon(selectedFile.tipo)}</div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Visualização não disponível</p>
                    </div>
                  )}
                </div>
                {/* Informações adicionais */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    <User size={18} className="text-gray-400 dark:text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Usuário</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{selectedFile.usuario}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar size={18} className="text-gray-400 dark:text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Data</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {new Date(selectedFile.data).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </div>
                </div>
                {/* Botões de ação */}
                <div className="flex items-center gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => handleDownload(selectedFile)}
                    className="flex-1 px-4 py-3 rounded-xl bg-primary-500 dark:bg-primary-600 text-white hover:bg-primary-600 dark:hover:bg-primary-700 transition-all font-medium flex items-center justify-center gap-2 shadow-lg shadow-primary-500/30"
                  >
                    <Download size={18} />
                    Baixar Arquivo
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
